import React, { useEffect } from 'react';
import styled from 'styled-components';
import { IoArrowDown } from 'react-icons/io5';
import { UserMessage } from '@/features/ai-agents/components/user-message';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AgentMessage } from '@/features/ai-agents/components/agent-message';

export interface ChatMessagesProps {
  showReasoning?: boolean;
  showTools?: boolean;

  isLocalHost?: boolean;
}

export function ChatMessages({ showTools, showReasoning }: ChatMessagesProps) {
  const { scrollRef, messages, processedEvents, isProcessing } =
    useChatSession();

  const [showScrollButton, setShowScrollButton] = React.useState(false);
  useEffect(() => {
    const container = scrollRef.current as HTMLElement;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      setShowScrollButton(distanceFromBottom > 40);
    };

    container.addEventListener(`scroll`, handleScroll);
    setTimeout(handleScroll, 1000);

    // eslint-disable-next-line consistent-return
    return () => {
      container.removeEventListener(`scroll`, handleScroll);
    };
  }, [scrollRef]);

  const handleScrollToBottom = () => {
    const container = scrollRef.current as HTMLElement;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: `smooth` });
    }
  };

  return (
    <>
      <Wrapper ref={scrollRef}>
        {messages?.value?.map((msg, index) => {
          const isLatestAndProcessing =
            isProcessing.value && index === messages.value.length - 1;
          return msg.role === `user` ? (
            <UserMessage key={messageKey(msg, index)} message={msg} />
          ) : (
            <AgentMessage
              key={messageKey(msg, index)}
              message={msg}
              showReasoning={showReasoning}
              showTools={showTools}
              isProcessing={isProcessing}
              processedEvents={
                isLatestAndProcessing ? processedEvents : undefined
              }
            />
          );
        })}
      </Wrapper>
      <ScrollToBottomButton
        onClick={handleScrollToBottom}
        visible={showScrollButton}
        aria-label="Scroll to bottom"
      >
        <IoArrowDown size={20} />
      </ScrollToBottomButton>
    </>
  );
}

export function messageKey(message: AiChatMessage, index: number): string {
  return `${message.role}-${message.created_at}-${index}`;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
  width: 100%;
  overflow-y: scroll;
  max-height: 98vh;
  padding-top: 3rem;
  padding-bottom: 20rem;

  mask-image: linear-gradient(
    var(--mask-direction, to bottom),
    hsl(0 0% 0% / 0),
    rgb(248 249 251 / 1) 5%,
    rgb(248 249 251 / 1) 85%,
    hsl(0 0% 0% / 0)
  );
`;

const ScrollToBottomButton = styled.button<{ visible: boolean }>`
  outline: none;
  border: none;
  appearance: none;

  position: absolute;
  bottom: 14rem;
  left: 50%;
  background-color: white;
  box-shadow: 0 2px 10px rgb(0 0 0 / 0.1), 0 8px 10px rgb(0 0 0 / 0.06);
  border-radius: 100%;
  z-index: 10;
  height: 3.5rem;
  width: 3.5rem;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;

  transition: opacity 0.3s, transform 0.3s;
  opacity: ${(p) => (p.visible ? 1 : 0)};
  pointer-events: ${(p) => (p.visible ? `auto` : `none`)};
  transform: translateX(-50%) translateY(${(p) => (p.visible ? `0` : `40px`)});
`;
